import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown } from 'lucide-react';

import { apiClient, type FinancialCategory } from '@/lib/api-client';
import { cn } from '@/lib/utils';
import { AnchoredPopover } from '@/components/ui/anchored-popover';

/**
 * Filtro de categoria em arvore, multi-selecao, agrupado por Receitas/Despesas
 * com subcategorias indentadas sob o pai (D09 Financeiro).
 *
 * UX do pai: marcar uma categoria-pai marca visualmente TODOS os filhos junto
 * (cascata) e mostra "inclui subcategorias" ao lado do nome; desmarcar desfaz
 * os dois juntos. Marcar filhos individualmente e livre — o pai fica com
 * checkbox indeterminado enquanto so parte deles estiver marcada, e "sobe"
 * pra checked sozinho quando o ultimo filho fecha o conjunto. O array
 * enviado via `value`/`onChange` sempre reflete literalmente os checkboxes
 * marcados (pai + filhos quando cascata) — o backend expande pai->filhos de
 * qualquer forma, entao mandar so o pai ou pai+filhos da no mesmo resultado;
 * mandar tudo e so o jeito mais direto de manter o componente sem estado
 * proprio alem do `value` controlado de fora.
 */

export interface CategoryFilterProps {
  /** Ids de categoria marcados (pais e/ou filhos, sem duplicar semantica — ver nota acima). */
  value: string[];
  onChange: (ids: string[]) => void;
  className?: string;
  'aria-label'?: string;
}

interface CategoryTreeItem {
  id: string;
  name: string;
  color: string;
  active: boolean;
  children: CategoryTreeItem[];
}

type CategoryGroups = Record<'income' | 'expense', CategoryTreeItem[]>;
type CheckState = 'checked' | 'unchecked' | 'indeterminate';

const GROUP_LABEL: Record<'income' | 'expense', string> = {
  income: 'Receitas',
  expense: 'Despesas',
};
const GROUP_ORDER = ['income', 'expense'] as const;

function normalizeText(v: string): string {
  return v.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();
}

function buildGroups(categories: FinancialCategory[]): CategoryGroups {
  const byId = new Map<string, CategoryTreeItem>();
  for (const c of categories) {
    byId.set(c.id, {
      id: c.id,
      name: c.name,
      color: c.color,
      active: c.active,
      children: [],
    });
  }
  const groups: CategoryGroups = { income: [], expense: [] };
  for (const c of categories) {
    const node = byId.get(c.id);
    if (!node) continue;
    if (c.parentId && byId.has(c.parentId)) {
      byId.get(c.parentId)!.children.push(node);
    } else {
      groups[c.type].push(node);
    }
  }
  for (const type of GROUP_ORDER) {
    groups[type].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
    for (const parent of groups[type]) {
      parent.children.sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
    }
  }
  return groups;
}

// Invariante mantido por toggleParent/toggleLeaf: quando um node tem filhos,
// `selected.has(parent.id)` so e true se TODOS os filhos tambem estao. Por
// isso o calculo aqui nao precisa reconferir a contagem toda vez.
function parentState(parent: CategoryTreeItem, selected: Set<string>): CheckState {
  if (parent.children.length === 0) {
    return selected.has(parent.id) ? 'checked' : 'unchecked';
  }
  if (selected.has(parent.id)) return 'checked';
  const anyChild = parent.children.some((c) => selected.has(c.id));
  return anyChild ? 'indeterminate' : 'unchecked';
}

// Ids visiveis apos o filtro de busca. null = sem busca ativa (mostra tudo).
// Pai casa -> mostra ele + todos os filhos. So filho casa -> mostra o pai
// como contexto + so os filhos que casaram.
function computeVisibleIds(groups: CategoryGroups, needle: string): Set<string> | null {
  const trimmed = needle.trim();
  if (!trimmed) return null;
  const normalizedNeedle = normalizeText(trimmed);
  const visible = new Set<string>();
  for (const type of GROUP_ORDER) {
    for (const parent of groups[type]) {
      const parentMatches = normalizeText(parent.name).includes(normalizedNeedle);
      const matchingChildren = parent.children.filter((c) =>
        normalizeText(c.name).includes(normalizedNeedle),
      );
      if (parentMatches) {
        visible.add(parent.id);
        for (const c of parent.children) visible.add(c.id);
      } else if (matchingChildren.length > 0) {
        visible.add(parent.id);
        for (const c of matchingChildren) visible.add(c.id);
      }
    }
  }
  return visible;
}

export function CategoryFilter({
  value,
  onChange,
  className,
  'aria-label': ariaLabel,
}: CategoryFilterProps) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const triggerRef = React.useRef<HTMLButtonElement>(null);

  const categoriesQ = useQuery({
    queryKey: ['financial', 'categories', 'list', 'filter-tree'],
    queryFn: () => apiClient.financialCategoriesList(),
    staleTime: 1000 * 60,
  });

  const groups = React.useMemo(
    () => buildGroups(categoriesQ.data ?? []),
    [categoriesQ.data],
  );

  const selected = React.useMemo(() => new Set(value), [value]);
  const visibleIds = React.useMemo(
    () => computeVisibleIds(groups, search),
    [groups, search],
  );

  function toggleLeaf(parent: CategoryTreeItem, leafId: string) {
    const next = new Set(selected);
    if (next.has(leafId)) next.delete(leafId);
    else next.add(leafId);
    // Ressincroniza o pai: entra no set sse TODOS os filhos entraram, mantendo
    // o invariante que parentState() depende.
    const allChildrenIn = parent.children.every((c) => next.has(c.id));
    if (allChildrenIn) next.add(parent.id);
    else next.delete(parent.id);
    onChange(Array.from(next));
  }

  function toggleParent(parent: CategoryTreeItem) {
    const next = new Set(selected);
    const isFull = parentState(parent, selected) === 'checked';
    if (isFull) {
      next.delete(parent.id);
      for (const c of parent.children) next.delete(c.id);
    } else {
      next.add(parent.id);
      for (const c of parent.children) next.add(c.id);
    }
    onChange(Array.from(next));
  }

  const label =
    selected.size === 0
      ? 'Categorias'
      : `${selected.size} selecionada${selected.size > 1 ? 's' : ''}`;

  const hasAnyVisible = visibleIds === null || visibleIds.size > 0;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={ariaLabel ?? 'Filtrar por categoria'}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={cn(
          'flex h-9 items-center justify-between gap-2 rounded-xl border bg-[var(--surface)] px-2.5 text-sm transition',
          'border-[var(--border)] text-[var(--fg)]',
          'focus:outline-none focus:ring-2 focus:ring-[var(--accent)]',
          open && 'ring-2 ring-[var(--accent)]',
          className,
        )}
      >
        <span className={cn('truncate', selected.size === 0 && 'text-[var(--fg-muted)]')}>
          {label}
        </span>
        <ChevronDown
          className={cn(
            'h-3.5 w-3.5 shrink-0 text-[var(--fg-muted)] transition',
            open && 'rotate-180',
          )}
        />
      </button>

      <AnchoredPopover anchorRef={triggerRef} open={open} onClose={() => setOpen(false)} width={288}>
        <div className="border-b border-[var(--popover-border,var(--card-border))] p-1.5">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar categoria..."
            className={cn(
              'flex h-8 w-full rounded-lg border bg-[var(--surface)] px-2.5 text-sm',
              'border-[var(--border)] text-[var(--fg)]',
              'placeholder:text-[var(--fg-muted)]',
              'focus:outline-none focus:ring-2 focus:ring-[var(--accent)]',
            )}
          />
        </div>

        {selected.size > 0 && (
          <button
            type="button"
            onClick={() => onChange([])}
            className="w-full border-b border-[var(--popover-border,var(--card-border))] px-3 py-1.5 text-left text-xs text-[var(--fg-muted)] transition hover:text-[var(--fg)]"
          >
            Limpar seleção ({selected.size})
          </button>
        )}

        <div className="max-h-72 overflow-y-auto py-1">
          {categoriesQ.isLoading && (
            <p className="px-3 py-4 text-center text-xs text-[var(--fg-muted)]">Carregando...</p>
          )}
          {!categoriesQ.isLoading && !hasAnyVisible && (
            <p className="px-3 py-4 text-center text-xs text-[var(--fg-muted)]">
              Nenhuma categoria encontrada.
            </p>
          )}
          {!categoriesQ.isLoading &&
            GROUP_ORDER.map((type) => {
              const roots = groups[type].filter(
                (p) => visibleIds === null || visibleIds.has(p.id),
              );
              if (roots.length === 0) return null;
              return (
                <div key={type}>
                  <div className="px-3 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-[var(--fg-muted)]">
                    {GROUP_LABEL[type]}
                  </div>
                  {roots.map((parent) => (
                    <React.Fragment key={parent.id}>
                      <CategoryCheckboxRow
                        label={parent.name}
                        color={parent.color}
                        inactive={!parent.active}
                        hint={parent.children.length > 0 ? 'inclui subcategorias' : undefined}
                        state={parentState(parent, selected)}
                        onToggle={() => toggleParent(parent)}
                        indent={0}
                      />
                      {parent.children
                        .filter((c) => visibleIds === null || visibleIds.has(c.id))
                        .map((child) => (
                          <CategoryCheckboxRow
                            key={child.id}
                            label={child.name}
                            color={child.color}
                            inactive={!child.active}
                            state={selected.has(child.id) ? 'checked' : 'unchecked'}
                            onToggle={() => toggleLeaf(parent, child.id)}
                            indent={1}
                          />
                        ))}
                    </React.Fragment>
                  ))}
                </div>
              );
            })}
        </div>
      </AnchoredPopover>
    </>
  );
}

function CategoryCheckboxRow({
  label,
  color,
  state,
  onToggle,
  indent,
  inactive,
  hint,
}: {
  label: string;
  color: string;
  state: CheckState;
  onToggle: () => void;
  indent: number;
  inactive?: boolean;
  hint?: string;
}) {
  const ref = React.useRef<HTMLInputElement>(null);
  React.useEffect(() => {
    if (ref.current) ref.current.indeterminate = state === 'indeterminate';
  }, [state]);

  return (
    <label
      className={cn(
        'flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm transition hover:bg-[color-mix(in_oklch,var(--fg)_6%,transparent)]',
        indent > 0 && 'pl-8',
      )}
    >
      <input
        ref={ref}
        type="checkbox"
        checked={state === 'checked'}
        onChange={onToggle}
        className="h-3.5 w-3.5 shrink-0 rounded border-[var(--border)] accent-[var(--accent)]"
      />
      <span className="inline-block h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: color }} />
      <span className={cn('flex-1 truncate text-[var(--fg)]', inactive && 'text-[var(--fg-muted)]')}>
        {label}
      </span>
      {inactive && <span className="shrink-0 text-xs text-[var(--fg-muted)]">(inativa)</span>}
      {hint && <span className="shrink-0 text-[10px] text-[var(--fg-muted)]">{hint}</span>}
    </label>
  );
}
